/**
 * Branches archive — search, filter, grid/map toggle, Google Maps.
 *
 * Relies on window.branchData (array) set inline in archive-branch.php.
 */

import Fuse from "fuse.js";
import { initPopover } from "./popover.js";
import { HIVE_MAP_STYLES } from "./map-styles.js";

if (document.querySelector(".branch-archive")) {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const state = {
    view: "grid", // 'grid' | 'map'
    search: "",
    filters: new Set(), // active filter values
    map: null,
    markers: [], // { branchId, marker, infoWindow }
    nearMeActive: false,
    nearMeLat: null,
    nearMeLng: null,
  };

  // ---------------------------------------------------------------------------
  // Fuzzy search index
  // ---------------------------------------------------------------------------

  const fuse = new Fuse(window.branchData || [], {
    keys: [
      { name: "name", weight: 2 },
      { name: "address", weight: 1 },
    ],
    threshold: 0.35, // 0 = exact, 1 = match anything — 0.35 is comfortably fuzzy
    ignoreLocation: true,
  });

  // ---------------------------------------------------------------------------
  // DOM refs
  // ---------------------------------------------------------------------------

  const searchInput = document.getElementById("branch-search");
  const nearMeBtn = document.getElementById("branch-near-me");
  const filterToggle = document.getElementById("branch-filter-toggle");
  const filterPanel = document.getElementById("branch-filter-panel");
  const filterCheckboxes = filterPanel
    ? Array.from(filterPanel.querySelectorAll(".branch-filter-checkbox"))
    : [];
  const activeFiltersBar = document.getElementById("branch-active-filters");
  const resultsStatus = document.getElementById("branch-results-status");
  const gridView = document.getElementById("branch-grid-view");
  const mapView = document.getElementById("branch-map-view");
  const gridNoResults = document.getElementById("branch-no-results");
  const mapNoResults = document.getElementById("branch-map-no-results");
  const viewBtns = Array.from(document.querySelectorAll(".branch-view-btn"));

  // ---------------------------------------------------------------------------
  // Time helpers
  // ---------------------------------------------------------------------------

  // "Open Now" / "Open on Sundays" both use values precomputed by PHP
  // (archive-branch.php). PHP is the only place that knows about active
  // closure alerts and holiday closures, so we never recompute open-state
  // here — that would silently bypass all of that logic.
  function isOpenNow(branch) {
    return !!branch.isOpen;
  }

  function isOpenSundays(branch) {
    return !!branch.openSundays;
  }

  // ---------------------------------------------------------------------------
  // Haversine distance (km)
  // ---------------------------------------------------------------------------

  function haversine(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ---------------------------------------------------------------------------
  // Filter logic
  // ---------------------------------------------------------------------------

  function branchPassesFilters(branch, fuseMatchIds) {
    const { filters } = state;

    // Fuzzy search — fuseMatchIds is null when no search is active
    if (fuseMatchIds !== null && !fuseMatchIds.has(branch.id)) return false;

    // Hours filters
    if (filters.has("open-now") && !isOpenNow(branch)) return false;
    if (filters.has("open-sundays") && !isOpenSundays(branch)) return false;

    // Amenity filters — branch must have ALL selected amenities
    for (const filter of filters) {
      if (filter === "open-now" || filter === "open-sundays") continue;
      if (!branch.amenities || !branch.amenities.includes(filter)) return false;
    }

    return true;
  }

  function applyFilters() {
    const data = window.branchData || [];

    // Build a match ID set from search query (null = no search active).
    // Short queries (1–2 chars) use a loose prefix check: all query characters must
    // appear in order within the first (query.length * 3) characters of the field.
    // This catches slow/mis-typed prefixes like "fe" → "fletcher" without Fuse's
    // bitap penalty being too harsh at low lengths.
    // Fuzzy matching kicks in at 3+ chars where it adds real value.
    let fuseMatchIds = null;
    if (state.search) {
      if (state.search.length <= 2) {
        // Every character in the query must appear somewhere in the name or address
        const allCharsPresent = (str, query) =>
          [...query].every((ch) => str.includes(ch));
        fuseMatchIds = new Set(
          data
            .filter(
              (b) =>
                allCharsPresent(b.name.toLowerCase(), state.search) ||
                (b.address &&
                  allCharsPresent(b.address.toLowerCase(), state.search)),
            )
            .map((b) => b.id),
        );
      } else {
        fuseMatchIds = new Set(fuse.search(state.search).map((r) => r.item.id));
      }
    }

    // Build a set of visible branch IDs for this filter state
    const visibleIds = new Set();
    for (const branch of data) {
      if (branchPassesFilters(branch, fuseMatchIds)) {
        visibleIds.add(branch.id);
      }
    }

    // Show/hide grid tiles
    if (gridView) {
      let gridVisible = 0;
      gridView.querySelectorAll("[data-branch-id]").forEach((card) => {
        const id = parseInt(card.dataset.branchId, 10);
        const show = visibleIds.has(id);
        card.hidden = !show;
        if (show) gridVisible++;
      });
      if (gridNoResults) gridNoResults.hidden = gridVisible > 0;
    }

    // Show/hide map list items
    if (mapView) {
      let mapVisible = 0;
      mapView.querySelectorAll("[data-branch-id]").forEach((card) => {
        const id = parseInt(card.dataset.branchId, 10);
        const show = visibleIds.has(id);
        card.hidden = !show;
        if (show) mapVisible++;
      });
      if (mapNoResults) mapNoResults.hidden = mapVisible > 0;
    }

    // Show/hide map markers and re-fit bounds to visible set
    if (state.map) {
      const bounds = new google.maps.LatLngBounds();
      let visibleMarkerCount = 0;

      state.markers.forEach(({ branchId, marker }) => {
        const show = visibleIds.has(branchId);
        marker.map = show ? state.map : null;
        if (show && marker.position) {
          bounds.extend(marker.position);
          visibleMarkerCount++;
        }
      });

      if (visibleMarkerCount > 0) {
        // Set maxZoom before fitBounds so the single animation stops at 14 naturally.
        // Restore after the map settles to avoid blocking manual zoom-in.
        state.map.setOptions({ maxZoom: 14 });
        state.map.fitBounds(bounds);
        google.maps.event.addListenerOnce(state.map, "idle", () => {
          state.map.setOptions({ maxZoom: 20 });
        });
      } else {
        state.map.panTo({ lat: 34.7465, lng: -92.2896 });
        state.map.setZoom(10);
      }
    }

    // Announce result count to screen readers
    if (resultsStatus) {
      const count = visibleIds.size;
      if (state.search || state.filters.size > 0) {
        resultsStatus.textContent =
          count === 0
            ? "No branches match your search."
            : `${count} branch${count === 1 ? "" : "es"} found.`;
      } else {
        resultsStatus.textContent = "";
      }
    }

    updateActiveFiltersBar();
  }

  // ---------------------------------------------------------------------------
  // Active filters bar
  // ---------------------------------------------------------------------------

  const FILTER_LABELS = {
    "open-now": "Open Now",
    "open-sundays": "Open on Sundays",
    "3D Printer": "3D Printers",
    "Book Lockers": "Book Lockers",
    "Community Fridge": "Community Fridge",
    "Community Pantry": "Community Pantry",
    "Memory Lab": "DIY Memory Lab",
    "Podcast Spaces": "Podcast Spaces",
  };

  // Sentinel data-filter value for the "Near Me" chip — used so the
  // chip click handler can tell it apart from a regular checkbox-backed
  // amenity/hours filter (no checkbox to untick; clears geolocation
  // sort instead).
  const NEAR_ME_FILTER = "__near-me";

  function updateActiveFiltersBar() {
    if (!activeFiltersBar) return;

    const chips = [];

    // Near Me sort gets its own chip so the user can undo it just
    // like any other active filter.
    if (state.nearMeActive) {
      chips.push(
        `<button type="button" class="filter-bar-btn active branch-filter-chip" data-filter="${NEAR_ME_FILTER}" aria-pressed="true" aria-label="Remove sort: Near Me">Near Me</button>`,
      );
    }

    state.filters.forEach((val) => {
      const label = FILTER_LABELS[val] || val;
      chips.push(
        `<button type="button" class="filter-bar-btn active branch-filter-chip" data-filter="${val}" aria-pressed="true" aria-label="Remove filter: ${label}">${label}</button>`,
      );
    });

    if (chips.length === 0) {
      activeFiltersBar.hidden = true;
      activeFiltersBar.innerHTML = "";
      return;
    }

    activeFiltersBar.hidden = false;
    activeFiltersBar.innerHTML = chips.join("");

    // Wire up chip clicks — the whole chip is the remove control now.
    activeFiltersBar
      .querySelectorAll(".branch-filter-chip")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const val = btn.dataset.filter;

          if (val === NEAR_ME_FILTER) {
            clearNearMe();
            return;
          }

          state.filters.delete(val);

          // Uncheck the corresponding checkbox
          const checkbox =
            filterPanel &&
            filterPanel.querySelector(`[value="${CSS.escape(val)}"]`);
          if (checkbox) checkbox.checked = false;

          applyFilters();
        });
      });
  }

  // ---------------------------------------------------------------------------
  // Near Me
  // ---------------------------------------------------------------------------

  function handleNearMe() {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    nearMeBtn.setAttribute("aria-busy", "true");
    nearMeBtn.disabled = true;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        nearMeBtn.removeAttribute("aria-busy");
        nearMeBtn.disabled = false;

        state.nearMeActive = true;
        state.nearMeLat = pos.coords.latitude;
        state.nearMeLng = pos.coords.longitude;

        sortByDistance();
        updateActiveFiltersBar();
      },
      () => {
        nearMeBtn.removeAttribute("aria-busy");
        nearMeBtn.disabled = false;
        alert(
          "Unable to retrieve your location. Please check your browser permissions.",
        );
      },
      { timeout: 10000 },
    );
  }

  function sortByDistance() {
    if (!state.nearMeLat || !state.nearMeLng) return;

    const data = window.branchData || [];

    // Build distance map
    const distances = {};
    data.forEach((branch) => {
      if (branch.lat && branch.lng) {
        distances[branch.id] = haversine(
          state.nearMeLat,
          state.nearMeLng,
          branch.lat,
          branch.lng,
        );
      } else {
        distances[branch.id] = Infinity;
      }
    });

    // Sort grid tiles
    const gridContainer = gridView && gridView.querySelector(".branch-grid");
    if (gridContainer) {
      const tiles = Array.from(
        gridContainer.querySelectorAll("[data-branch-id]"),
      );
      tiles.sort(
        (a, b) =>
          (distances[a.dataset.branchId] || Infinity) -
          (distances[b.dataset.branchId] || Infinity),
      );
      tiles.forEach((tile) => gridContainer.appendChild(tile));
    }

    // Sort map list items
    const mapList = document.getElementById("branch-map-list");
    if (mapList) {
      const items = Array.from(mapList.querySelectorAll("[data-branch-id]"));
      items.sort(
        (a, b) =>
          (distances[a.dataset.branchId] || Infinity) -
          (distances[b.dataset.branchId] || Infinity),
      );
      items.forEach((item) => mapList.appendChild(item));
    }

    // Pan map to user location and fit bounds
    if (state.map && state.nearMeLat && state.nearMeLng) {
      state.map.panTo({ lat: state.nearMeLat, lng: state.nearMeLng });
    }

    if (resultsStatus) {
      resultsStatus.textContent = "Results sorted by nearest location.";
    }
  }

  /**
   * Clear the "Near Me" sort and restore the original alphabetical
   * order from window.branchData. Called when the user clicks the
   * Near Me chip in the active-filters bar to remove it.
   */
  function clearNearMe() {
    state.nearMeActive = false;
    state.nearMeLat = null;
    state.nearMeLng = null;

    // Sort branch tiles + map list back into the order PHP produced
    // (Main Library pinned first, then alphabetical), keyed by the
    // index of each entry in window.branchData.
    const data = window.branchData || [];
    const order = new Map();
    data.forEach((branch, i) => order.set(String(branch.id), i));

    const sortByOriginalOrder = (a, b) =>
      (order.get(a.dataset.branchId) ?? Infinity) -
      (order.get(b.dataset.branchId) ?? Infinity);

    const gridContainer = gridView && gridView.querySelector(".branch-grid");
    if (gridContainer) {
      const tiles = Array.from(
        gridContainer.querySelectorAll("[data-branch-id]"),
      );
      tiles.sort(sortByOriginalOrder);
      tiles.forEach((tile) => gridContainer.appendChild(tile));
    }

    const mapList = document.getElementById("branch-map-list");
    if (mapList) {
      const items = Array.from(mapList.querySelectorAll("[data-branch-id]"));
      items.sort(sortByOriginalOrder);
      items.forEach((item) => mapList.appendChild(item));
    }

    if (resultsStatus) {
      resultsStatus.textContent = "Sorted alphabetically.";
    }

    updateActiveFiltersBar();
  }

  // ---------------------------------------------------------------------------
  // Grid / Map toggle
  // ---------------------------------------------------------------------------

  function switchView(view) {
    state.view = view;

    if (gridView) gridView.hidden = view !== "grid";
    if (mapView) mapView.hidden = view !== "map";

    viewBtns.forEach((btn) => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    // Trigger map resize when switching to map view
    if (view === "map" && state.map) {
      google.maps.event.trigger(state.map, "resize");
    }
  }

  // ---------------------------------------------------------------------------
  // Filter panel
  // ---------------------------------------------------------------------------

  // Popover behavior (open/close/escape/focus-trap) via global module.
  if (filterToggle && filterPanel) {
    initPopover({ popover: filterPanel, toggle: filterToggle });
  }

  // ---------------------------------------------------------------------------
  // Tooltip helpers
  // ---------------------------------------------------------------------------

  // Escape user-provided strings before inserting into innerHTML so a stray
  // `&` or `<` in a branch name doesn't break the markup AT depends on.
  function escapeHtml(str) {
    return String(str ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  }

  function buildTooltip(branch) {
    const statusClass = branch.isOpen ? "is-open" : "is-closed";

    const imageHtml = branch.image
      ? `<div class="branch-map-tooltip-image"><img src="${escapeHtml(branch.image)}" alt="" loading="lazy"></div>`
      : "";

    // External-view branches (and any branch the helper deemed status-less)
    // intentionally render with no status row.
    const statusHtml = branch.statusText
      ? `<div class="branch-map-tooltip-status">
        <span class="branch-status-text ${statusClass}">${branch.statusText}</span>
      </div>`
      : "";

    return `<div class="branch-map-tooltip">
    ${imageHtml}
    <div class="branch-map-tooltip-body">
      <a class="branch-map-tooltip-name" href="${escapeHtml(branch.permalink)}">${escapeHtml(branch.name)}</a>
      ${statusHtml}
    </div>
  </div>`;
  }

  // ---------------------------------------------------------------------------
  // Google Maps init (called as async callback by Maps API)
  // ---------------------------------------------------------------------------

  window.initBranchMap = function () {
    const canvas = document.getElementById("branch-map-canvas");
    if (!canvas) return;

    const data = window.branchData || [];

    // Default center: Little Rock, AR
    const defaultCenter = { lat: 34.7465, lng: -92.2896 };

    const map = new google.maps.Map(canvas, {
      zoom: 10,
      center: defaultCenter,
      mapId: "branch-map", // Required for AdvancedMarkerElement
      // 'greedy' silences the "use ⌘/Ctrl + scroll" overlay; pairing it with
      // `scrollwheel: false` keeps page scroll intact when the cursor is
      // over the map (no zoom from wheel/trackpad).
      gestureHandling: "greedy",
      scrollwheel: false,
      mapTypeControl: false,
      streetViewControl: false,
      styles: HIVE_MAP_STYLES,
    });

    state.map = map;

    // Single shared InfoWindow — reused across markers
    const infoWindow = new google.maps.InfoWindow({ maxWidth: 280 });
    state.infoWindow = infoWindow;

    // Move focus into the tooltip when it opens so keyboard users can reach the link
    infoWindow.addListener("domready", () => {
      const link = document.querySelector(".branch-map-tooltip-name");
      if (link) link.focus();
    });

    const bounds = new google.maps.LatLngBounds();
    let hasCoords = false;

    data.forEach((branch) => {
      if (!branch.lat || !branch.lng) return;

      const position = { lat: branch.lat, lng: branch.lng };
      bounds.extend(position);
      hasCoords = true;

      // SVG teardrop pin — styled via .branch-map-pin in _branches.scss
      const pinEl = document.createElement("div");
      pinEl.className = "branch-map-pin";
      pinEl.setAttribute("aria-label", branch.name);
      pinEl.innerHTML = `<svg class="branch-map-pin-svg" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 0C7.2 0 0 7.2 0 16C0 27 16 42 16 42C16 42 32 27 32 16C32 7.2 24.8 0 16 0Z"/>
    </svg>
    <span class="branch-map-pin-num">${branch.index}</span>`;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        map,
        content: pinEl,
        title: branch.name,
      });

      marker.addListener("click", () => {
        infoWindow.setContent(buildTooltip(branch));
        infoWindow.open({ map, anchor: marker });
        setActivePin(branch.id);
        highlightListItem(branch.id);
      });

      state.markers.push({ branchId: branch.id, marker, pinEl });
    });

    // Close InfoWindow when clicking the map background
    map.addListener("click", () => infoWindow.close());

    // Clear active-pin state when the InfoWindow is dismissed
    infoWindow.addListener("closeclick", () => setActivePin(null));
    map.addListener("click", () => setActivePin(null));

    // Wire up list items to activate their corresponding pin on focus/hover
    document
      .querySelectorAll(".branch-map-item[data-branch-id]")
      .forEach((item) => {
        const id = parseInt(item.dataset.branchId, 10);
        item.addEventListener("focusin", () => setActivePin(id));
        item.addEventListener("focusout", (e) => {
          if (!item.contains(e.relatedTarget)) setActivePin(null);
        });
        item.addEventListener("mouseenter", () => setActivePin(id));
        item.addEventListener("mouseleave", () => {
          if (!item.contains(document.activeElement)) setActivePin(null);
        });
      });

    if (hasCoords) {
      map.fitBounds(bounds);
    }
  };

  // Load the Maps API only after `initBranchMap` is defined above. Doing this
  // from JS (rather than an inline `<script async callback=...>` in PHP) avoids
  // a race where Maps finishes loading before the bundle has parsed and fires
  // the callback against an undefined function. Guarded by `__hiveMapsLoaded`
  // so the API is never injected twice (Maps refuses to load more than once).
  if (
    document.getElementById("branch-map-canvas") &&
    !window.__hiveMapsLoaded
  ) {
    window.__hiveMapsLoaded = true;
    const key = window.HIVE_GMAPS_KEY || "";
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&callback=initBranchMap&loading=async&libraries=marker`;
    s.async = true;
    document.head.appendChild(s);
  }

  // Toggle the `.is-active` class on a single pin (or clear all when id is null).
  // Used for visual parity between the hovered/focused list item and its pin, and
  // while an InfoWindow is open.
  function setActivePin(branchId) {
    state.markers.forEach(({ branchId: id, pinEl }) => {
      if (!pinEl) return;
      pinEl.classList.toggle("is-active", id === branchId);
    });
  }

  function highlightListItem(branchId) {
    // Switch to map view if not already there
    if (state.view !== "map") switchView("map");

    const item = document.getElementById(`branch-map-item-${branchId}`);
    if (!item) return;

    // Remove existing highlights
    document
      .querySelectorAll(".branch-map-item.is-highlighted")
      .forEach((el) => {
        el.classList.remove("is-highlighted");
      });

    item.classList.add("is-highlighted");
    item.scrollIntoView({ behavior: "smooth", block: "nearest" });
    item.focus({ preventScroll: true });
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  // Search
  if (searchInput) {
    let searchTimer;
    searchInput.addEventListener("input", () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        state.search = searchInput.value.toLowerCase().trim();
        applyFilters();
      }, 150);
    });
  }

  // Near Me
  if (nearMeBtn) {
    nearMeBtn.addEventListener("click", handleNearMe);
  }

  // Filter checkboxes
  filterCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) {
        state.filters.add(checkbox.value);
      } else {
        state.filters.delete(checkbox.value);
      }
      applyFilters();
    });
  });

  // Escape: close InfoWindow (filter panel escape is handled by the popover module)
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    // Only close InfoWindow if the filter panel is not open (popover handles its own escape)
    if (filterPanel && !filterPanel.hidden) return;
    if (state.infoWindow) {
      state.infoWindow.close();
      setActivePin(null);
    }
  });

  // View toggle buttons
  viewBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      switchView(btn.dataset.view);
    });
  });
} // end branch-archive guard
